/**
 * <isaboko-variant-filter> — the size filter on the variations grid.
 *
 * Markup: snippets/isaboko-variant-size-rail.liquid for the chips,
 * sections/main-product-variations.liquid for the element itself, the results
 * bar and the cards.
 *
 * Every other filter in this theme is decided on the server: the shopper's
 * choice is in the query string, Shopify hands the section a filtered set, and
 * the page comes back already correct. That is not available here. The
 * variations grid is a product template, and a product template can neither
 * ask Shopify to filter variants nor read a query parameter of its own —
 * Liquid is given the path, never the query string. So the server renders
 * every variant it would have rendered anyway, and this class decides which of
 * them are on screen.
 *
 * The choice still lives in the URL (?size=...), which is what keeps this from
 * being a control that only exists while you look at it: the URL survives a
 * reload, a share and the back button, and it is the single place the state is
 * kept — nothing on the instance is authoritative.
 *
 * Filtering is per page. The grid is paginated, so a selected size can have
 * cards on page two that this page has never seen; the count in the bar is
 * therefore drawn from two numbers with different scopes — how many cards are
 * showing here, and how many the whole product has in that size, which the
 * server counted and parked on the chip. Saying "showing 6 results of 14"
 * across a two-page filter is the honest reading of it, and matches what the
 * collection page's bar means by the same words.
 */
const ISABOKO_SIZE_PARAM = 'size';

class IsabokoVariantFilter extends HTMLElement {
  connectedCallback() {
    // Scoped to the rail: the "show all sizes" link in the empty state carries
    // the same data-variant-filter-value so the click handler picks it up, but
    // it is not a chip and must not be given a chip's selected state.
    this.chips = Array.from(this.querySelectorAll('.isaboko-facet-rail [data-variant-filter-value]'));
    this.items = Array.from(this.querySelectorAll('[data-variant-size]'));
    this.shownCount = this.querySelector('[data-variant-filter-shown]');
    this.totalCount = this.querySelector('[data-variant-filter-total]');
    this.emptyState = this.querySelector('[data-variant-filter-empty]');

    if (!this.chips.length || !this.items.length) return;

    this.onClick = this.handleClick.bind(this);
    this.onPopState = this.handlePopState.bind(this);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);

    // From the URL, not from the markup: a shopper arriving on a shared or
    // reloaded ?size= link should land on the filtered grid, and the server
    // has no way to have rendered it that way.
    this.apply(this.valueFromUrl(), { writeUrl: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
  }

  /**
   * A size only counts if a chip offers it. Anything else in ?size= — a typo, a
   * stale link to a size that has since sold out, a value from another product
   * — resolves to no filter rather than to an empty grid the shopper cannot
   * explain.
   */
  valueFromUrl() {
    const requested = new URLSearchParams(window.location.search).get(ISABOKO_SIZE_PARAM) || '';
    if (!requested) return '';
    return this.chips.some((chip) => chip.dataset.variantFilterValue === requested) ? requested : '';
  }

  handleClick(event) {
    const chip = event.target.closest('[data-variant-filter-value]');
    if (!chip) return;

    // A chip's href is its own filtered view of this page, and that URL works
    // on arrival — the filter reads it back out of the query string. So a
    // shopper opening a size in a new tab or a new window is asking for
    // something this page can actually give them, and the browser is left to
    // do it. Only the plain click is ours to take.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Following the link on a plain click would reload the page to no purpose:
    // the server would send back the same grid, filtered by the same script.
    event.preventDefault();

    // Clicking the selected chip clears it — that is what the ✕ on it means.
    // "All" carries the empty value, so clicking it clears by the same path.
    const value = chip.dataset.variantFilterValue;
    this.apply(value === this.current ? '' : value, { writeUrl: true });
  }

  handlePopState() {
    this.apply(this.valueFromUrl(), { writeUrl: false });
  }

  apply(value, { writeUrl }) {
    this.current = value;

    let shown = 0;
    this.items.forEach((item) => {
      const matches = !value || item.dataset.variantSize === value;
      item.hidden = !matches;
      if (matches) shown += 1;
    });

    let selected = null;
    this.chips.forEach((chip) => {
      const isSelected = chip.dataset.variantFilterValue === value;
      if (isSelected) selected = chip;
      chip.classList.toggle('isaboko-facet-rail__chip--active', isSelected);
      // Links have no pressed state; aria-current is how a link says "this one
      // is the view you are looking at".
      if (isSelected) {
        chip.setAttribute('aria-current', 'true');
      } else {
        chip.removeAttribute('aria-current');
      }
    });

    if (this.shownCount) this.shownCount.textContent = shown;
    if (this.totalCount && selected) this.totalCount.textContent = selected.dataset.variantFilterCount;

    // Only reachable with a size chosen and its other cards on another page:
    // a chip is never drawn for a size with nothing behind it.
    if (this.emptyState) this.emptyState.hidden = shown > 0;

    this.updatePaginationLinks(value);
    if (writeUrl) this.updateUrl(value);
  }

  /**
   * The pagination links were built by the server before any size was chosen,
   * so left alone they drop the filter on the way to page two. Rewriting the
   * one parameter keeps the rest of the URL — view, page, anything else —
   * exactly as Shopify built it.
   */
  updatePaginationLinks(value) {
    this.querySelectorAll('.pagination__item[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      if (value) {
        url.searchParams.set(ISABOKO_SIZE_PARAM, value);
      } else {
        url.searchParams.delete(ISABOKO_SIZE_PARAM);
      }
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  }

  /**
   * replaceState, not pushState. A size chip is a way of looking at one page,
   * not a new page: stacking history entries would turn the browser's back
   * button into an undo for the rail and take several presses to leave a page
   * the shopper only visited once. popstate is still listened for, so a ?size=
   * entry that reached history another way is honoured.
   */
  updateUrl(value) {
    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(ISABOKO_SIZE_PARAM, value);
    } else {
      url.searchParams.delete(ISABOKO_SIZE_PARAM);
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

customElements.define('isaboko-variant-filter', IsabokoVariantFilter);
